import React, { useEffect, useState } from "react";
import { colorLogoWhite } from "../../assets";
import { Link } from "react-router-dom";
import { Minus, ArrowRight } from "lucide-react";
import SocialIcons from "./SocialIcons";

import Joi from "joi";
import axios from "axios";
import confetti from "canvas-confetti";
import helpers from "../../lib/crypto";
import { useFunctionContext } from "../context/FunctionalContext";

const API_URL = import.meta.env.VITE_API_URL;

const Footer = () => {
  const { ORG_ID } = useFunctionContext();

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({ email: "" });
  const [error, setError] = useState("");

  const handleEmail = (e) => {
    const { name, value } = e.target;
    const formatted = value.replace(/[^a-zA-Z0-9@.]/g, "");
    setFormData({ ...formData, [name]: formatted });

    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formatted);
    setError(isValid ? "" : "Invalid email");
  };

  const add_leads = (data) => {
    const schema = Joi.object({
      key: Joi.string().required(),
      source: Joi.string().required(),
      email: Joi.string()
        .trim()
        .min(10)
        .max(55)
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          "string.email": "Please enter a valid email address",
        }),
      admin_type: Joi.string().optional().allow("", null),
      organisation_id: Joi.string().required(),
      route_action: Joi.number().valid(1).required(),
      status: Joi.string().trim().valid("New").required(),
      next_follow_up: Joi.date().iso().allow("", null),
    });

    return schema.validate(data);
  };

  const doSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!formData.email) {
      setError("Please fill all the fields");
      setLoading(false);
      return;
    }

    const payload = {
      key: "scanglobal",
      route_action: 1,
      admin_type: "1",
      organisation_id: ORG_ID,
      email: formData.email,
      status: "New",
      source: "Holdings",
      next_follow_up: new Date().toISOString(),
    };

    const { error: validationError } = add_leads(payload);
    if (validationError) {
      setError(validationError.details[0].message);
      setLoading(false);
      return;
    }

    let encrypted;
    try {
      encrypted = helpers.encryptobj(payload);
    } catch {
      setError("Encryption failed.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/admin/add_update_lead`,
        { enc: encrypted },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        setSuccessMessage("Your message has been sent successfully!");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        setError("");
        setFormData({ email: "" });
      } else {
        setError(
          response.data?.message || "Something went wrong on the server"
        );
      }
    } catch (err) {
      console.error("Axios error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <footer className="text-[#BCBCBC] bg-gradient-to-t from-gold/5 to-transparent w-full relative z-10 mb-14 lg:mb-0">
      <div className="mt-auto w-full max-w-[85rem] py-8 px-4 sm:px-6 lg:px-8 lg:pt-16 mx-auto">
        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[repeat(4,1fr)_20rem] gap-6">
          <div className="">
            <Link
              className="flex-none text-xl font-semibold text-white focus:outline-none focus:opacity-80"
              // to="/"
              aria-label="Brand"
            >
              <img
                src={colorLogoWhite}
                height={120}
                width={120}
                alt="main logo"
                loading="lazy"
                className="relative object-cover w-24 h-24 md:w-32 md:h-32"
              />
            </Link>
          </div>

          <div className="">
            <Minus size={16} className="text-gold" />
            <h4 className="font-semibold text-gray-100 text-lg md:text-xl">
              About Us
            </h4>
            <div className="mt-3 space-y-2">
              <p>
                <Link
                  to="/about-us"
                  onClick={() => window.scrollTo(0, 0)}
                  className="group relative inline-flex items-center text-gray-400 text-sm sm:text-base focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    About ScanGlobal
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>
                  <ArrowRight
                    size={12}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 hidden sm:inline-block"
                  />
                </Link>
              </p>
              <p>
                <Link
                  to="/about-us#team"
                  onClick={() =>
                    window.scrollTo(0, document.body.scrollHeight / 2)
                  }
                  className="group relative inline-flex items-center text-gray-400 text-sm sm:text-base focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Leadership Team
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>
                  <ArrowRight
                    size={12}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 hidden sm:inline-block"
                  />
                </Link>
              </p>
            </div>
          </div>

          <div className="">
            <Minus size={16} className="text-gold" />
            <h4 className="font-semibold text-gray-100 text-lg md:text-xl">
              Our Expertise
            </h4>
            <div className="mt-3 space-y-2">
              <p>
                <Link
                  to="/mergers-and-acquisitions"
                  className="group relative inline-flex items-center text-gray-400 text-sm sm:text-base focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Mergers & Acquisitions
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>
                  <ArrowRight
                    size={12}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 hidden sm:inline-block"
                  />
                </Link>
              </p>
              <p>
                <Link
                  to="/business-advisory"
                  className="group relative inline-flex items-center text-gray-400 text-sm sm:text-base focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Finance Business Advisory
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>
                  <ArrowRight
                    size={12}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 hidden sm:inline-block"
                  />
                </Link>
              </p>
              <p>
                <Link
                  to="/real-estate"
                  className="group relative inline-flex items-center text-gray-400 text-sm sm:text-base focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Real Estate
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>
                  <ArrowRight
                    size={12}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 hidden sm:inline-block"
                  />
                  <span className="inline-block text-xs bg-red-400/15 text-red-600 py-1 px-2 rounded-lg ml-2">
                    Hot 🔥
                  </span>
                </Link>
              </p>
              <p>
                <Link
                  to="/tax-consulting"
                  className="group relative inline-flex items-center text-gray-400 text-sm sm:text-base focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Tax Consulting
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>
                  <ArrowRight
                    size={12}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300 hidden sm:inline-block"
                  />
                </Link>
              </p>
            </div>
          </div>
          <div className="">
            <Minus size={16} className="text-gold" />
            <h4 className="font-semibold text-gray-100 text-lg md:text-xl">
              Visit
            </h4>
            <address className="not-italic text-gray-400 mt-3 space-y-4">
              {/* Dubai Office */}
              <div>
                <p className="font-bold text-base text-gray-600">
                  Dubai Office
                </p>
                <p className="text-sm">
                  Office 805, Donna Towers, <br />
                  Dubai Silicon Oasis, Dubai, UAE
                </p>
              </div>

              {/* Canada Office */}
              <div>
                <p className="font-bold text-base text-gray-600">
                  Canada Office
                </p>
                <p className="text-sm">
                  Unit 208/209, 3485 Rebecca St, <br />
                  Oakville, Ontario, Canada L6L 0H4
                </p>
              </div>

              {/* Contact Email */}
              <div>
                <p className="font-bold text-base text-gray-600">Email</p>
                <a
                  href="mailto:admin@scanglobal.me"
                  className="text-sm text-gold hover:underline"
                >
                  admin@scanglobal.me
                </a>
              </div>
            </address>
          </div>

          <div className="">
            <Minus size={16} className="text-gold" />
            <h4 className="font-semibold text-gray-100 text-lg md:text-xl">
              Stay Up To Date
            </h4>
            <form action="#" method="POST" onSubmit={doSubmit}>
              <div className="mt-4 flex flex-col gap-3 rounded-lg p-3 bg-neutral-900">
                <div className="w-full">
                  <div className="ai-input-container">
                    <div className="ai-input-inner">
                      <input
                        name="email"
                        placeholder="Enter your email"
                        className="ai-input"
                        type="text"
                        onChange={handleEmail}
                        value={formData.email}
                        // maxLength={25}
                      />

                      <label className="ai-label">
                        {loading ? "sending...." : "Subscribe"}
                      </label>
                      <button className="ai-button">
                        {loading ? (
                          <span className="h-4 w-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></span>
                        ) : (
                          "↳"
                        )}
                      </button>
                      <div className="digital-noise">
                        <span
                          style={{
                            left: "15%",
                            top: "20%",
                            animationDelay: "0s",
                          }}
                          className="noise-bit"
                        >
                          1
                        </span>
                        <span
                          style={{
                            left: "30%",
                            top: "60%",
                            animationDelay: "-0.2s",
                          }}
                          className="noise-bit"
                        >
                          0
                        </span>
                        <span
                          style={{
                            left: "45%",
                            top: "40%",
                            animationDelay: "-0.4s",
                          }}
                          className="noise-bit"
                        >
                          1
                        </span>
                        <span
                          style={{
                            left: "60%",
                            top: "80%",
                            animationDelay: "-0.6s",
                          }}
                          className="noise-bit"
                        >
                          0
                        </span>
                        <span
                          style={{
                            left: "75%",
                            top: "30%",
                            animationDelay: "-0.8s",
                          }}
                          className="noise-bit"
                        >
                          1
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Feedback messages */}
                  {error && (
                    <p className="text-sm mt-3 text-red-400 font-medium text-start">
                      {error}
                    </p>
                  )}

                  {successMessage && (
                    <p className="mt-4 text-sm text-green-400 font-medium text-start">
                      {successMessage}
                    </p>
                  )}
                </div>
                <p className="mt-2 text-[9px] sm:text-sm text-gray-400 ">
                  Get professional advice and industry trends delivered to your
                  inbox.
                </p>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-6 sm:mt-10 flex flex-col sm:flex-row justify-start lg:justify-around items-start sm:items-center gap-4">
          <p className="text-xs sm:text-sm text-gray-200">
            © {new Date().getFullYear()} Scanglobal.
          </p>
          <SocialIcons />
        </div>
      </div>
      <div className="bg-[radial-gradient(closest-side,theme(colors.gold),theme(colors.gold),transparent)] h-[2px]"></div>
    </footer>
  );
};

export default Footer;
